import React, { useState, useEffect, useMemo } from "react";
import { useNavigation } from "@react-navigation/native";
import { useValidation } from "../../hooks";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Card,
  CardContent,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Skeleton,
  Tooltip,
  Collapse,
  Stack,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import {
  LocationOn as LocationOnIcon,
  TrendingUp as TrendingUpIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  InfoOutlined as InfoOutlinedIcon,
  Storage as StorageIcon,
  Description as DescriptionIcon,
  Calculate as CalculateIcon,
  EventBusy as EventBusyIcon,
  EventAvailable as EventAvailableIcon,
  ReportProblem as ReportProblemIcon,
} from "@mui/icons-material";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { BACKGROUND_COLORS, ESTADO_ORO_COLORS, BRAND_COLORS, TEXT_COLORS } from "../../style/colors.js";
import { calculateMetalCoverage, calculateMetalNeedHorizons, excelSerialToDate } from "../../services/utils/calendarUtils.js";
import { getMetalsData, getMetalsNeedsData, getCordobaStockData, getExcelDetailsData } from "../../services/ConnectDataBase.js";
import { SCREEN_IDS, typeConnect } from "../../constants/Constants.js";
import { calculateFineMetals } from "../../constants/MetalPurityFactors.js";
import ComponentExcelFilters from "../Metales/ComponentExcelFilters.js";
import ComponentMetalsCalendar from "../Metales/ComponentMetalsCalendar.js";
import { getGoldPrice } from "../../services/GoldPriceService.js";
import { getPlatinumPrice } from "../../services/PlatinumPriceService.js";
import { getSilverPrice } from "../../services/SilverPriceService.js";
import { getTitaniumPrice } from "../../services/TitaniumPriceService.js";
import {
  calcSingleMetalPurchaseCost,
  formatEuroAmount,
  formatEuroPerGram,
  buildSingleMetalPurchaseTooltip,
} from "../../services/utils/metalPurchaseCost.js";

const fmtNum = (n) =>
  (n || 0).toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Configuración de skeletons de carga
const SKELETON_CONFIG = {
  summaryRows: 3,        // Número de filas en la tabla de resumen (2 datos + 1 total)
  tableRows: 5,          // Número de filas en las tablas de ubicación
  height: 20,            // Altura de los skeletons en px
  widthLabel: "100%",    // Ancho del skeleton de etiqueta
  widthValue: "100%",    // Ancho del skeleton de valor
};

export default function ComponentEstadoOro() {
  const navigation = useNavigation();
  const { validateData } = useValidation({ autoDispatch: true, validationType: 2 });

  // Abre la pantalla de la MO igual que el escáner (pedidos que empiezan por 4 u 8)
  const handleOrderClick = async (mo) => {
    if (!mo) return;
    const code = String(mo).trim().replace(/'/g, "-");
    try {
      const fullData = await validateData(code);
      if (fullData && fullData.mo) {
        navigation.navigate("Info", { data: fullData, incidencia: false });
      }
    } catch (e) {
      console.error("Error al abrir la MO", code, e);
    }
  };

  const [cordobaData, setCordobaData] = useState({
    goldStock: [],
    findingsStock: [],
    workInProgress: [],
  });

  const [indiaData, setIndiaData] = useState({
    goldStock: [],
    findingsStock: [],
    platinumStock: [],
    silverStock: [],
  });

  const [excelDetailsData, setExcelDetailsData] = useState([]);
  const [dynamicRequiredMetals, setDynamicRequiredMetals] = useState({
    cordoba: { gold: 0, platinum: 0, silver: 0 },
    euroshine: { gold: 0, platinum: 0, silver: 0 },
  });
  const [lastUpdate, setLastUpdate] = useState(new Date().toLocaleString());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTables, setExpandedTables] = useState({
    "Stock de Oro": false,
    "Stock de Platino": false,
    "Stock de Fornituras": false,
    "Trabajo en Proceso": false,
  });
  const [isIndiaExpanded, setIsIndiaExpanded] = useState(true);
  const [showHelp, setShowHelp] = useState(false);
  const [needHorizonHelpAnchor, setNeedHorizonHelpAnchor] = useState(null);
  const [horizonOrdersModal, setHorizonOrdersModal] = useState(null);
  const [metalPrices, setMetalPrices] = useState({
    goldEurPerGram: null,
    platinumEurPerGram: null,
    silverEurPerGram: null,
    titaniumEurPerGram: null,
    goldSource: null,
    platinumSource: null,
    silverSource: null,
    titaniumSource: null,
    updatedAt: null,
    loading: true,
  });

  const loadMetalPrices = async () => {
    setMetalPrices((prev) => ({ ...prev, loading: true }));
    try {
      const [goldData, platinumData, silverData, titaniumData] = await Promise.allSettled([
        getGoldPrice(),
        getPlatinumPrice(),
        getSilverPrice(),
        getTitaniumPrice(),
      ]);
      const gold = goldData.status === "fulfilled" ? goldData.value : null;
      const platinum = platinumData.status === "fulfilled" ? platinumData.value : null;
      const silver = silverData.status === "fulfilled" ? silverData.value : null;
      const titanium = titaniumData.status === "fulfilled" ? titaniumData.value : null;

      setMetalPrices({
        goldEurPerGram: gold?.eurPerGram ?? null,
        platinumEurPerGram: platinum?.eurPerGram ?? null,
        silverEurPerGram: silver?.eurPerGram ?? null,
        titaniumEurPerGram: titanium?.eurPerGram ?? null,
        goldSource: gold?.source ?? null,
        platinumSource: platinum?.source ?? null,
        silverSource: silver?.source ?? null,
        titaniumSource: titanium?.source ?? null,
        updatedAt: new Date(),
        loading: false,
      });
    } catch {
      setMetalPrices({
        goldEurPerGram: null,
        platinumEurPerGram: null,
        silverEurPerGram: null,
        titaniumEurPerGram: null,
        goldSource: null,
        platinumSource: null,
        silverSource: null,
        titaniumSource: null,
        updatedAt: null,
        loading: false,
      });
    }
  };

  useEffect(() => {
    loadMetalPrices();
  }, []);

  useEffect(() => {
    const loadMetalsData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const metalsData = await getMetalsData(
          SCREEN_IDS.ESTADO_METALES,
          typeConnect[0]
        );

        const needsData = await getMetalsNeedsData(
          SCREEN_IDS.ESTADO_METALES,
          typeConnect[0]
        );
        const cordobaStockData = await getCordobaStockData(
          SCREEN_IDS.ESTADO_METALES,
          typeConnect[0]
        );

        const excelDetails = await getExcelDetailsData(
          SCREEN_IDS.ESTADO_METALES,
          typeConnect[0]
        );


        if (metalsData) {
          setCordobaData({
            goldStock: metalsData.cordoba?.goldStock || [],
            findingsStock: metalsData.cordoba?.findingsStock || [],
            workInProgress: metalsData.cordoba?.workInProgress || [],
            needs: needsData?.Cordoba || {},
            stock: cordobaStockData || []
          });
          if (
            metalsData["Gold Stock"] ||
            metalsData["Findings Stock"] ||
            metalsData["Platinum Stock"] ||
            metalsData["Silver Stock"]
          ) {
            setIndiaData({
              goldStock: metalsData["Gold Stock"] || [],
              findingsStock: metalsData["Findings Stock"] || [],
              platinumStock: metalsData["Platinum Stock"] || [],
              silverStock: metalsData["Silver Stock"] || [],
              needs: needsData?.Euroshine || {}
            });
          }
          
          if (excelDetails && Array.isArray(excelDetails)) {
            setExcelDetailsData(excelDetails);
          }
          
          setLastUpdate(new Date().toLocaleString());
        } else {
          setError("No se pudieron cargar los datos de metales");
        }
      } catch (err) {
        console.error("Error al cargar datos:", err);
        setError("Error al conectar con el servidor");
      } finally {
        setIsLoading(false);
      }
    };
    loadMetalsData();
  }, []);

  const metalNeedHorizons = useMemo(
    () => calculateMetalNeedHorizons(excelDetailsData),
    [excelDetailsData]
  );

  useEffect(() => {
    if (metalNeedHorizons.invalidOrders.length > 0) {
      console.warn(
        "[Estado Oro] Pedidos excluidos por fecha de entrega inválida o sin metal:",
        metalNeedHorizons.invalidOrders
      );
    }
  }, [metalNeedHorizons]);

  const calculateTotals = (data, tableType = "standard") => {
    if (!data || !Array.isArray(data)) {
      return tableType === "findings"
        ? { clsQty: 0, clsWt: 0, pureWt: 0, clsValue: 0 }
        : { clsWt: 0, pureWt: 0 };
    }

    return data.reduce(
      (totals, item) => {
        let clsWt = 0;
        if (!isNaN(item.Cls_Wt)) {
          clsWt = parseFloat(item.Cls_Wt);
        }

        let pureWt = 0;
        if (tableType === "findings") {
          pureWt = clsWt;

          // Calcular cantidad para findings
          let clsQty = 0;
          if (!isNaN(item.Cls_Qty)) {
            clsQty = parseFloat(item.Cls_Qty);
          }
          totals.clsQty += clsQty;

          // Calcular valor para findings
          let clsValue = 0;
          if (!isNaN(item.Cls_Value)) {
            clsValue = parseFloat(item.Cls_Value);
          }
          totals.clsValue += clsValue;
        } else {
          if (!isNaN(item.Pure_Wt)) {
            pureWt = parseFloat(item.Pure_Wt);
          }
        }

        totals.clsWt += clsWt;
        totals.pureWt += pureWt;
        return totals;
      },
      tableType === "findings"
        ? { clsQty: 0, clsWt: 0, pureWt: 0, clsValue: 0 }
        : { clsWt: 0, pureWt: 0 }
    );
  };

  const renderTable = (
    title,
    data,
    color = "primary",
    tableType = "standard"
  ) => {
    // Calcular totales por tipo de findings si es una tabla de findings
    const getFindingsByType = (findingsData) => {
      const goldFindings = findingsData.filter(item => item.Fnd_Type === "Gold Findings");
      const silverFindings = findingsData.filter(item => item.Fnd_Type === "Silver Findings");
      const platinumFindings = findingsData.filter(item => item.Fnd_Type === "Platinum Findings");
      const otherFindings = findingsData.filter(item => item.Fnd_Type === "Other Findings");
      
      return {
        gold: calculateTotals(goldFindings, "findings"),
        silver: calculateTotals(silverFindings, "findings"),
        platinum: calculateTotals(platinumFindings, "findings"),
        other: calculateTotals(otherFindings, "findings")
      };
    };

    const findingsByType = tableType === "findings" ? getFindingsByType(data) : null;
    
    // Guardar el total original antes del filtrado para la lógica de expansión
    const originalDataLength = data.length;
    
    // Filtrar filas que contengan "Total" en Loc_Desc (vienen del servidor con NaN)
    let filteredData = data.filter(
      (item) =>
        !item.Loc_Desc ||
        (typeof item.Loc_Desc === "string" &&
          !item.Loc_Desc.toLowerCase().includes("total"))
    );

    // Filtrar filas con cantidad cero para tablas de findings
    if (tableType === "findings") {
      filteredData = filteredData.filter(
        (item) => {
          const cantidad = parseFloat(item.Cls_Qty || 0);
          return cantidad > 0;
        }
      );
    }

    // Ordenar por tipo de findings si es una tabla de findings
    if (tableType === "findings") {
      filteredData = filteredData.sort((a, b) => {
        const typeA = a.Fnd_Type || "";
        const typeB = b.Fnd_Type || "";
        return typeA.localeCompare(typeB);
      });
    }

    const displayData =
      !expandedTables[title] && originalDataLength > 10
        ? filteredData.slice(0, 10)
        : filteredData;

    const totals = calculateTotals(filteredData, tableType);
    const showTotals = expandedTables[title] || originalDataLength <= 10;

    const getHeaders = () => {
      switch (tableType) {
        case "findings":
          return [
            { key: "Fnd_Type", label: "Tipo", width: 100, align: "left" },
            { key: "RM_Code", label: "Código", width: 80, align: "left" },
            { key: "Cls_Qty", label: "Cant.", width: 70, align: "right" },
            { key: "Cls_Wt", label: "Peso (g)", width: 80, align: "right" },
            { key: "Cls_Value", label: "Valor", width: 80, align: "right" },
          ];
        case "gold":
        case "platinum":
        default:
          return [
            { key: "Loc_Desc", label: "Ubicación", width: 150, align: "left" },
            {
              key: "Cls_Wt",
              label: "Total (g)",
              width: 80,
              align: "right",
            },
            {
              key: "Pure_Wt",
              label: "Puro (g)",
              width: 80,
              align: "right",
            },
          ];
      }
    };

    const headers = getHeaders();

    return (
      <Card sx={{ mb: 0.5, boxShadow: 3, borderRadius: 2, minHeight: 400 }}>
        <CardContent sx={{ p: 1.5, height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Sección superior con altura fija */}
          <Box sx={{ minHeight: 40, mb: 0.2 }}>
          {/* Primera línea: Título y controles */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
            <MaterialCommunityIcons 
              name="gold" 
                size={14} 
              style={{ 
                  marginRight: 4,
                color: color === 'primary' ? ESTADO_ORO_COLORS.ICON_PRIMARY : ESTADO_ORO_COLORS.ICON_SECONDARY
              }}
            />
            <Typography
                variant="subtitle2"
                sx={{ fontWeight: "bold", color: `${color}.main`, fontSize: "0.75rem" }}
            >
              {title}
            </Typography>
              {originalDataLength > 10 ? (
              <IconButton
                onClick={() =>
                  setExpandedTables((prev) => ({
                    ...prev,
                    [title]: !prev[title],
                  }))
                }
                sx={{ ml: 1, color: `${color}.main` }}
                size="small"
              >
                {expandedTables[title] ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </IconButton>
              ) : (
                <Box sx={{ ml: 1, width: 24, height: 24 }} />
            )}
              {originalDataLength > 10 ? (
              <Typography
                variant="caption"
                sx={{ ml: 1, color: "text.secondary" }}
              >
                {expandedTables[title]
                  ? `Mostrando todos (${originalDataLength})`
                  : `Mostrando 10 de ${originalDataLength}`}
              </Typography>
              ) : (
                <Typography
                  variant="caption"
                  sx={{ ml: 1, color: "transparent" }}
                >
                  Mostrando 10 de 10
              </Typography>
            )}
          </Box>
          
            {/* Segunda línea: Chips por tipo de tabla */}
            <Box sx={{ display: "flex", alignItems: "center", mb: 0.1, flexWrap: "wrap", gap: 0.2 }}>
              {tableType === "findings" && findingsByType ? (
                <>
              <Chip
                label={`Gold: ${findingsByType.gold.clsWt.toLocaleString("es-ES", {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                })}g`}
                color={color}
                variant="outlined"
                size="small"
                    sx={{ fontWeight: "bold", fontSize: "0.55rem", height: "16px" }}
              />
              <Chip
                label={`Silver: ${findingsByType.silver.clsWt.toLocaleString("es-ES", {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                })}g`}
                color={color}
                variant="outlined"
                size="small"
                    sx={{ fontWeight: "bold", fontSize: "0.55rem", height: "16px" }}
              />
              <Chip
                label={`Platinum: ${findingsByType.platinum.clsWt.toLocaleString("es-ES", {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                })}g`}
                color={color}
                variant="outlined"
                size="small"
                    sx={{ fontWeight: "bold", fontSize: "0.55rem", height: "16px" }}
              />
              <Chip
                label={`Other: ${findingsByType.other.clsWt.toLocaleString("es-ES", {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                })}g`}
                color={color}
                variant="outlined"
                size="small"
                    sx={{ fontWeight: "bold", fontSize: "0.55rem", height: "16px" }}
                  />
                </>
              ) : tableType === "gold" ? (
                <>
                  <Chip
                    label={`Total: ${totals.clsWt.toLocaleString("es-ES", {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })}g`}
                    color={color}
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: "bold", fontSize: "0.55rem", height: "16px" }}
                  />
                  <Chip
                    label={`Puro: ${totals.pureWt.toLocaleString("es-ES", {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })}g`}
                    color={color}
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: "bold", fontSize: "0.55rem", height: "16px" }}
                  />
                </>
              ) : tableType === "platinum" ? (
                <>
                  <Chip
                    label={`Total: ${totals.clsWt.toLocaleString("es-ES", {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })}g`}
                    color={color}
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: "bold", fontSize: "0.55rem", height: "16px" }}
                  />
                  <Chip
                    label={`Puro: ${totals.pureWt.toLocaleString("es-ES", {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })}g`}
                    color={color}
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: "bold", fontSize: "0.55rem", height: "16px" }}
                  />
                </>
              ) : (
                // Para tablas estándar (como work in progress)
                <>
                  <Chip
                    label={`Total: ${totals.clsWt.toLocaleString("es-ES", {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })}g`}
                    color={color}
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: "bold", fontSize: "0.55rem", height: "16px" }}
                  />
                  <Chip
                    label={`Puro: ${totals.pureWt.toLocaleString("es-ES", {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })}g`}
                    color={color}
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: "bold", fontSize: "0.55rem", height: "16px" }}
                  />
                  <Chip
                    label=""
                    color={color}
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: "bold", fontSize: "0.6rem", height: "18px", visibility: "hidden" }}
                  />
                  <Chip
                    label=""
                    color={color}
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: "bold", fontSize: "0.6rem", height: "18px", visibility: "hidden" }}
                  />
                </>
              )}
            </Box>
            <Typography variant="caption" sx={{ display: "block", color: "text.secondary", fontSize: "0.62rem", lineHeight: 1.3 }}>
              {tableType === "findings"
                ? "Fornituras en stock por tipo. Peso = gramos en bruto."
                : tableType === "gold"
                ? "Oro en stock por ubicación. Total = bruto; Puro = fino 999."
                : tableType === "platinum"
                ? "Platino en stock por ubicación. Total = bruto; Puro = fino 999."
                : "Piezas actualmente en fabricación (trabajo en proceso)."}
            </Typography>
          </Box>

          <TableContainer 
            component={Paper} 
            sx={{ 
              flexGrow: 1,
              borderRadius: 1,
              border: `1px solid ${ESTADO_ORO_COLORS.TABLE_BORDER}`,
              overflow: "hidden"
            }}
          >
            <Table size="small" stickyHeader sx={{ minWidth: 400 }}>
              <TableHead>
                <TableRow>
                  {headers.map((header) => (
                    <TableCell
                      key={header.key}
                      sx={{
                        backgroundColor: ESTADO_ORO_COLORS.ICON_PRIMARY,
                        fontWeight: "bold !important",
                        fontFamily:
                          '"Roboto", "Helvetica", "Arial", sans-serif !important',
                        minWidth: header.width,
                        textAlign: header.align || "left",
                        fontSize: "0.8rem",
                        padding: "4px 8px",
                        color: ESTADO_ORO_COLORS.TABLE_HEADER_TEXT
                      }}
                    >
                      {header.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayData.map((row, index) => (
                  <TableRow key={index} hover>
                    {headers.map((header) => (
                      <TableCell
                        key={header.key}
                        align={header.align || "left"}
                        sx={{
                          fontFamily:
                            header.key.includes("Wt") ||
                            header.key.includes("Value") ||
                            header.key.includes("Qty")
                              ? "monospace !important"
                              : '"Roboto", "Helvetica", "Arial", sans-serif !important',
                          fontWeight:
                            header.key === "Pure_Wt"
                              ? "bold !important"
                              : "medium !important",
                          color:
                            header.key === "Pure_Wt"
                              ? "success.main !important"
                              : "inherit !important",
                          fontSize: "0.8rem",
                          padding: "4px 8px"
                        }}
                      >
                        {header.key.includes("Wt") ||
                        header.key.includes("Value") ||
                        header.key.includes("Qty")
                          ? parseFloat(row[header.key] || 0).toLocaleString(
                              "es-ES",
                              {
                                minimumFractionDigits: 1,
                                maximumFractionDigits: 1,
                              }
                            )
                          : row[header.key] || ""}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {showTotals && (
                  <TableRow sx={{ backgroundColor: "grey.100" }}>
                    {tableType === "findings" ? (
                      <>
                        <TableCell
                          sx={{
                            fontWeight: "bold !important",
                            fontFamily:
                              '"Roboto", "Helvetica", "Arial", sans-serif !important',
                            fontSize: "0.8rem",
                            padding: "4px 8px"
                          }}
                        >
                          TOTAL
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: "bold !important",
                            fontFamily: "monospace !important",
                            fontSize: "0.75rem !important",
                            padding: "4px 8px"
                          }}
                        >
                          {totals.clsQty.toLocaleString("es-ES", {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          })}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: "bold !important",
                            fontFamily: "monospace !important",
                            fontSize: "0.75rem !important",
                            padding: "4px 8px"
                          }}
                        >
                          {totals.clsWt.toLocaleString("es-ES", {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          })}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: "bold !important",
                            fontFamily: "monospace !important",
                            fontSize: "0.75rem !important",
                            color: "success.main !important",
                            padding: "4px 8px"
                          }}
                        >
                          {totals.clsValue.toLocaleString("es-ES", {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          })}
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell
                          sx={{
                            fontWeight: "bold !important",
                            fontFamily:
                              '"Roboto", "Helvetica", "Arial", sans-serif !important',
                            fontSize: "0.8rem",
                            padding: "4px 8px"
                          }}
                        >
                          TOTAL
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: "bold !important",
                            fontFamily: "monospace !important",
                            fontSize: "0.75rem !important",
                            padding: "4px 8px"
                          }}
                        >
                          {totals.clsWt.toLocaleString("es-ES", {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          })}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: "bold !important",
                            fontFamily: "monospace !important",
                            fontSize: "0.75rem !important",
                            color: "success.main !important",
                            padding: "4px 8px"
                          }}
                        >
                          {totals.pureWt.toLocaleString("es-ES", {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          })}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  // Tarjeta de stock de un metal (oro/platino): total fino destacado + desglose por ubicación
  const renderMetalStockCard = (title, data, kind) => {
    const accent = kind === "platinum" ? "#8792A2" : "#D4AF37";
    const rows = (data || [])
      .filter(
        (item) =>
          !item.Loc_Desc ||
          (typeof item.Loc_Desc === "string" &&
            !item.Loc_Desc.toLowerCase().includes("total"))
      )
      .map((item) => ({
        loc: item.Loc_Desc || "—",
        gross: parseFloat(item.Cls_Wt || 0),
        fine: parseFloat(item.Pure_Wt || 0),
      }))
      .filter((r) => r.gross !== 0 || r.fine !== 0)
      .sort((a, b) => b.fine - a.fine);

    const totalFine = rows.reduce((s, r) => s + r.fine, 0);
    const totalGross = rows.reduce((s, r) => s + r.gross, 0);
    const maxFine = Math.max(...rows.map((r) => r.fine), 0.0001);

    return (
      <Card sx={{ boxShadow: 3, borderRadius: 2, height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Box sx={{ height: 4, backgroundColor: accent }} />
        <CardContent sx={{ p: 1.5, flexGrow: 1, display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
            <MaterialCommunityIcons name="gold" size={16} color={accent} />
            <Typography sx={{ fontWeight: "bold", fontSize: "0.78rem", color: ESTADO_ORO_COLORS.LOCATION_TEXT }}>
              {title}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
            <Typography sx={{ fontWeight: "bold", fontSize: "1.3rem", color: "success.main", fontFamily: "monospace", lineHeight: 1 }}>
              {fmtNum(totalFine)}g
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.66rem" }}>
              fino 999
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.66rem", mb: 1 }}>
            Bruto: {fmtNum(totalGross)}g · {rows.length} {rows.length === 1 ? "ubicación" : "ubicaciones"}
          </Typography>

          <Box sx={{ flexGrow: 1, overflow: "auto", maxHeight: 240, pr: 0.5 }}>
            {rows.length === 0 ? (
              <Typography variant="caption" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                Sin stock
              </Typography>
            ) : (
              rows.map((r, i) => (
                <Box key={i} sx={{ mb: 0.75 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.2 }}>
                    <Tooltip title={r.loc} arrow>
                      <Typography variant="caption" sx={{ fontSize: "0.68rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "60%" }}>
                        {r.loc}
                      </Typography>
                    </Tooltip>
                    <Tooltip title={`Fino ${fmtNum(r.fine)}g · Bruto ${fmtNum(r.gross)}g`} arrow>
                      <Typography variant="caption" sx={{ fontSize: "0.68rem", fontFamily: "monospace", fontWeight: "bold", whiteSpace: "nowrap", cursor: "help" }}>
                        {fmtNum(r.fine)}g
                      </Typography>
                    </Tooltip>
                  </Box>
                  <Box sx={{ height: 5, borderRadius: 3, backgroundColor: "#eef0f2", overflow: "hidden" }}>
                    <Box sx={{ width: `${(r.fine / maxFine) * 100}%`, height: "100%", backgroundColor: accent }} />
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Tarjeta de fornituras: agrupadas por tipo con peso y valor
  const renderFindingsCard = (title, data) => {
    const TYPE_META = {
      "Gold Findings": { label: "Oro", color: "#D4AF37" },
      "Silver Findings": { label: "Plata", color: "#9AA0A6" },
      "Platinum Findings": { label: "Platino", color: "#8792A2" },
      "Other Findings": { label: "Otras", color: "#90a4ae" },
    };

    const groups = {};
    (data || []).forEach((item) => {
      const qty = parseFloat(item.Cls_Qty || 0);
      if (qty <= 0) return;
      const type = item.Fnd_Type || "Other Findings";
      if (!groups[type]) groups[type] = { qty: 0, weight: 0, value: 0, items: 0 };
      groups[type].qty += qty;
      groups[type].weight += parseFloat(item.Cls_Wt || 0);
      groups[type].value += parseFloat(item.Cls_Value || 0);
      groups[type].items += 1;
    });

    const order = ["Gold Findings", "Silver Findings", "Platinum Findings", "Other Findings"];
    const entries = order
      .filter((t) => groups[t])
      .map((t) => [t, groups[t]]);
    const totalWeight = entries.reduce((s, [, g]) => s + g.weight, 0);
    const totalValue = entries.reduce((s, [, g]) => s + g.value, 0);
    const maxWeight = Math.max(...entries.map(([, g]) => g.weight), 0.0001);

    return (
      <Card sx={{ boxShadow: 3, borderRadius: 2, height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Box sx={{ height: 4, backgroundColor: "#607d8b" }} />
        <CardContent sx={{ p: 1.5, flexGrow: 1, display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
            <MaterialCommunityIcons name="diamond-stone" size={16} color="#607d8b" />
            <Typography sx={{ fontWeight: "bold", fontSize: "0.78rem", color: ESTADO_ORO_COLORS.LOCATION_TEXT }}>
              {title}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
            <Typography sx={{ fontWeight: "bold", fontSize: "1.3rem", color: "#455a64", fontFamily: "monospace", lineHeight: 1 }}>
              {fmtNum(totalWeight)}g
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.66rem" }}>
              peso total
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.66rem", mb: 1 }}>
            Valor: {fmtNum(totalValue)} · {entries.length} {entries.length === 1 ? "tipo" : "tipos"}
          </Typography>

          <Box sx={{ flexGrow: 1, overflow: "auto", maxHeight: 240, pr: 0.5 }}>
            {entries.length === 0 ? (
              <Typography variant="caption" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                Sin fornituras
              </Typography>
            ) : (
              entries.map(([type, g]) => {
                const meta = TYPE_META[type] || { label: type, color: "#90a4ae" };
                return (
                  <Box key={type} sx={{ mb: 0.85 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.2, alignItems: "center" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: meta.color, flexShrink: 0 }} />
                        <Typography variant="caption" sx={{ fontSize: "0.68rem", fontWeight: "bold" }}>
                          {meta.label}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: "0.62rem", color: "text.secondary" }}>
                          ({g.items})
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ fontSize: "0.68rem", fontFamily: "monospace", fontWeight: "bold", whiteSpace: "nowrap" }}>
                        {fmtNum(g.weight)}g
                      </Typography>
                    </Box>
                    <Box sx={{ height: 5, borderRadius: 3, backgroundColor: "#eef0f2", overflow: "hidden" }}>
                      <Box sx={{ width: `${(g.weight / maxWeight) * 100}%`, height: "100%", backgroundColor: meta.color }} />
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderLocationSection = (locationName, data, color) => {
    // Calcular totales por tipo de metal
    const goldTotal = calculateTotals(data.goldStock || [], "gold");
    const platinumTotal = calculateTotals(data.platinumStock || [], "platinum");
    const findingsTotal = calculateTotals(data.findingsStock || [], "findings");
    const workInProgressTotal = calculateTotals(data.workInProgress || [], "standard");

    // Calcular totales por tipo de findings
    const getFindingsByType = (findingsData) => {
      const goldFindings = findingsData.filter(item => item.Fnd_Type === "Gold Findings");
      const silverFindings = findingsData.filter(item => item.Fnd_Type === "Silver Findings");
      const platinumFindings = findingsData.filter(item => item.Fnd_Type === "Platinum Findings");
      const otherFindings = findingsData.filter(item => item.Fnd_Type === "Other Findings");
      
      return {
        gold: calculateTotals(goldFindings, "findings"),
        silver: calculateTotals(silverFindings, "findings"),
        platinum: calculateTotals(platinumFindings, "findings"),
        other: calculateTotals(otherFindings, "findings")
      };
    };

    const findingsByType = getFindingsByType(data.findingsStock || []);

    return (
      <Box sx={{ flex: 1, p: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 0.25, flexWrap: "wrap", gap: 1 }}>
          <LocationOnIcon sx={{ mr: 1, color: ESTADO_ORO_COLORS.LOCATION_ICON }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              color: ESTADO_ORO_COLORS.LOCATION_TEXT,
              textTransform: "uppercase",
              fontSize: "1.1rem"
            }}
          >
            {locationName}
          </Typography>
          
          {/* Flecha de expandir/colapsar solo para India */}
          {locationName === "Euroshine" && (
            <IconButton
              onClick={() => setIsIndiaExpanded(!isIndiaExpanded)}
              size="small"
              sx={{ 
                ml: 1,
                p: 0.5,
                color: ESTADO_ORO_COLORS.LOCATION_ICON,
                "&:hover": {
                  backgroundColor: "rgba(44, 62, 80, 0.1)"
                }
              }}
            >
              {isIndiaExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
          
          {/* Mostrar totales separados por tipo de metal */}
          {locationName === "Euroshine" ? (
            <>
              <Chip
                label={`Oro: ${goldTotal.pureWt.toLocaleString("es-ES", {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })}g`}
                variant="filled"
                size="small"
                sx={{ 
                  fontWeight: "bold", 
                  fontSize: "0.6rem", 
                  height: "18px",
                  backgroundColor: ESTADO_ORO_COLORS.CHIP_GOLD_BG,
                  color: ESTADO_ORO_COLORS.CHIP_GOLD_TEXT,
                  border: `1px solid ${ESTADO_ORO_COLORS.CHIP_GOLD_BORDER}`
                }}
              />
              <Chip
                label={`Platino: ${platinumTotal.pureWt.toLocaleString("es-ES", {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })}g`}
                variant="filled"
                size="small"
                sx={{ 
                  fontWeight: "bold", 
                  fontSize: "0.6rem", 
                  height: "18px",
                  backgroundColor: ESTADO_ORO_COLORS.CHIP_PLATINUM_BG,
                  color: ESTADO_ORO_COLORS.CHIP_PLATINUM_TEXT,
                  border: `1px solid ${ESTADO_ORO_COLORS.CHIP_PLATINUM_BORDER}`
                }}
              />
            </>
          ) : (
            <>
              <Chip
                label={`Oro: ${goldTotal.pureWt.toLocaleString("es-ES", {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })}g`}
                color={color}
                variant="outlined"
                size="small"
                sx={{ fontWeight: "bold", fontSize: "0.6rem", height: "18px" }}
              />
              <Chip
                label={`Fornituras: ${findingsTotal.clsWt.toLocaleString("es-ES", {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })}g`}
                color={color}
                variant="outlined"
                size="small"
                sx={{ fontWeight: "bold", fontSize: "0.6rem", height: "18px" }}
              />
              <Chip
                label={`WIP: ${workInProgressTotal.pureWt.toLocaleString("es-ES", {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })}g`}
                color={color}
                variant="outlined"
                size="small"
                sx={{ fontWeight: "bold", fontSize: "0.6rem", height: "18px" }}
              />
            </>
          )}
        </Box>

        <Typography
          variant="caption"
          sx={{ display: "block", color: "text.secondary", fontSize: "0.68rem", mb: 1.5, ml: 4 }}
        >
          Stock físico leído de la base de datos.
          {locationName === "Euroshine"
            ? " Pulsa la flecha para ver el desglose de oro, platino y fornituras."
            : ""}
        </Typography>

        {locationName === "Euroshine" ? (
          isIndiaExpanded && (
            <Box sx={{ 
              display: "flex", 
              flexDirection: { xs: "column", lg: "row" },
              gap: 1,
              flexWrap: "wrap"
            }}>
              <Box sx={{ flex: { xs: "1 1 100%", lg: "1 1 33%" } }}>
              {renderMetalStockCard("Stock de Oro", data.goldStock || [], "gold")}
              </Box>
              <Box sx={{ flex: { xs: "1 1 100%", lg: "1 1 33%" } }}>
              {renderMetalStockCard("Stock de Platino", data.platinumStock || [], "platinum")}
              </Box>
              <Box sx={{ flex: { xs: "1 1 100%", lg: "1 1 33%" } }}>
              {renderFindingsCard("Stock de Fornituras", data.findingsStock || [])}
              </Box>
            </Box>
          )
        ) : (
          <>
            {renderTable("Stock de Oro", data.goldStock || [], color, "gold")}
            {renderTable(
              "Stock de Fornituras",
              data.findingsStock || [],
              color,
              "findings"
            )}
            {renderTable(
              "Trabajo en Proceso",
              data.workInProgress || [],
              color,
              "standard"
            )}
          </>
        )}
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        {/* Skeletons para la tabla de resumen */}
        <Box sx={{ mb: 2, p: 1.5, backgroundColor: ESTADO_ORO_COLORS.SUMMARY_BG, borderRadius: 1, border: `1px solid ${ESTADO_ORO_COLORS.SUMMARY_BORDER}`, height: 220 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1 }} />
            <Skeleton variant="text" width={150} height={24} />
          </Box>
          <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[...Array(9)].map((_, i) => (
                    <TableCell
                      key={i}
                      sx={{
                        backgroundColor: ESTADO_ORO_COLORS.ICON_PRIMARY,
                        padding: "4px 8px",
                      }}>
                      <Skeleton variant="text" width="80%" height={20} sx={{ bgcolor: "rgba(255,255,255,0.3)" }} />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {[...Array(SKELETON_CONFIG.summaryRows)].map((_, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {[...Array(9)].map((_, colIndex) => (
                      <TableCell
                        key={colIndex}
                        align={colIndex === 0 ? "center" : "center"}
                        sx={{
                          padding: "4px 8px",
                          backgroundColor: rowIndex === SKELETON_CONFIG.summaryRows - 1 ? ESTADO_ORO_COLORS.TOTAL_CELL_BG : "transparent",
                        }}>
                        <Skeleton variant="text" width={SKELETON_CONFIG.widthValue} height={SKELETON_CONFIG.height} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Skeletons para las tablas de ubicación */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
          <Skeleton variant="circular" width={24} height={24} />
          <Skeleton variant="text" width={100} height={28} />
          <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width={100} height={24} sx={{ borderRadius: 1 }} />
        </Box>

        {/* Skeletons para las tablas de datos */}
        {[...Array(3)].map((_, tableIndex) => (
          <Card key={tableIndex} sx={{ mb: 0.5, boxShadow: 3, borderRadius: 2, minHeight: 400 }}>
            <CardContent sx={{ p: 1.5 }}>
              {/* Header del card */}
              <Box sx={{ minHeight: 40, mb: 0.2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                  <Skeleton variant="circular" width={14} height={14} sx={{ mr: 0.5 }} />
                  <Skeleton variant="text" width={120} height={20} />
                  <Skeleton variant="circular" width={24} height={24} sx={{ ml: 1 }} />
                  <Skeleton variant="text" width={150} height={16} sx={{ ml: 1 }} />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.2, flexWrap: "wrap" }}>
                  {[...Array(4)].map((_, chipIndex) => (
                    <Skeleton key={chipIndex} variant="rectangular" width={80} height={16} sx={{ borderRadius: 1 }} />
                  ))}
                </Box>
              </Box>

              {/* Tabla */}
              <TableContainer component={Paper} sx={{ borderRadius: 1, border: `1px solid ${ESTADO_ORO_COLORS.TABLE_BORDER}` }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {[...Array(5)].map((_, i) => (
                        <TableCell
                          key={i}
                          sx={{
                            backgroundColor: ESTADO_ORO_COLORS.ICON_PRIMARY,
                            padding: "4px 8px",
                          }}>
                          <Skeleton variant="text" width="70%" height={20} sx={{ bgcolor: "rgba(255,255,255,0.3)" }} />
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...Array(SKELETON_CONFIG.tableRows)].map((_, rowIndex) => (
                      <TableRow key={rowIndex}>
                        {[...Array(5)].map((_, colIndex) => (
                          <TableCell
                            key={colIndex}
                            sx={{
                              padding: "4px 8px",
                              backgroundColor: rowIndex % 2 === 0 ? "#ffffff" : "#f5f5f5",
                            }}>
                            <Skeleton variant="text" width={SKELETON_CONFIG.widthValue} height={SKELETON_CONFIG.height} />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                    {/* Fila de totales */}
                    <TableRow>
                      <TableCell sx={{ padding: "4px 8px", backgroundColor: "grey.100" }}>
                        <Skeleton variant="text" width="60%" height={SKELETON_CONFIG.height} />
                      </TableCell>
                      {[...Array(4)].map((_, i) => (
                        <TableCell
                          key={i}
                          sx={{ padding: "4px 8px", backgroundColor: "grey.100" }}>
                          <Skeleton variant="text" width={SKELETON_CONFIG.widthValue} height={SKELETON_CONFIG.height} />
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Última actualización: {lastUpdate}
        </Typography>
      </Box>
    );
  }

  const renderMetalPriceChip = (label, eurPerGram, source, color) => {
    if (metalPrices.loading) {
      return (
        <Chip size="small" label={`${label}: …`} sx={{ fontSize: "0.7rem", height: 22 }} />
      );
    }
    if (!eurPerGram) {
      return (
        <Chip
          size="small"
          label={`${label}: N/D`}
          color="warning"
          variant="outlined"
          sx={{ fontSize: "0.7rem", height: 22 }}
        />
      );
    }
    return (
      <Tooltip
        title={`Fuente: ${source || "API"}${metalPrices.updatedAt ? ` · ${metalPrices.updatedAt.toLocaleString("es-ES")}` : ""}`}
        arrow
      >
        <Chip
          size="small"
          icon={
            <MaterialCommunityIcons
              name="gold"
              size={14}
              color={color}
              style={{ marginLeft: 6 }}
            />
          }
          label={`${label}: ${formatEuroPerGram(eurPerGram)}`}
          sx={{
            fontSize: "0.7rem",
            height: 22,
            fontFamily: "monospace",
            fontWeight: "bold",
            backgroundColor: "rgba(255,255,255,0.85)",
          }}
        />
      </Tooltip>
    );
  };

  // Función para renderizar la tabla de resumen
  const renderSummaryTable = () => {
    const cordobaGoldStock = cordobaData.stock?.find(item => item.metal === "ORO")?.stock || 0;
    const cordobaPlatinumStock = cordobaData.stock?.find(item => item.metal === "PLATINO")?.stock || 0;
    
    const indiaGoldTotal = calculateTotals(indiaData.goldStock || [], "gold");
    const indiaPlatinumTotal = calculateTotals(indiaData.platinumStock || [], "platinum");
    const indiaSilverTotal = calculateTotals(indiaData.silverStock || [], "gold");
    const cordobaGoldRequired = dynamicRequiredMetals.cordoba.gold;
    const cordobaPlatinumRequired = dynamicRequiredMetals.cordoba.platinum;
    const cordobaSilverRequired = dynamicRequiredMetals.cordoba.silver || 0;
    const euroshineGoldRequired = dynamicRequiredMetals.euroshine.gold;
    const euroshinePlatinumRequired = dynamicRequiredMetals.euroshine.platinum;
    const euroshineSilverRequired = dynamicRequiredMetals.euroshine.silver || 0;
    
    // Calcular diferencias (Stock - Necesidad)
    const cordobaGoldDifference = parseFloat(cordobaGoldStock) - cordobaGoldRequired;
    const cordobaPlatinumDifference = parseFloat(cordobaPlatinumStock) - cordobaPlatinumRequired;
    const euroshineGoldDifference = indiaGoldTotal.pureWt - euroshineGoldRequired;
    const euroshinePlatinumDifference = indiaPlatinumTotal.pureWt - euroshinePlatinumRequired;

    const totalGoldDifference = cordobaGoldDifference + euroshineGoldDifference;
    const totalPlatinumDifference =
      cordobaPlatinumDifference + euroshinePlatinumDifference;

    // Provisión para fundición (casting): requerido + 28%
    const PROVISION_FACTOR = 1.28;
    const cordobaGoldRequiredPFC = cordobaGoldRequired * PROVISION_FACTOR;
    const cordobaPlatinumRequiredPFC = cordobaPlatinumRequired * PROVISION_FACTOR;
    const euroshineGoldRequiredPFC = euroshineGoldRequired * PROVISION_FACTOR;
    const euroshinePlatinumRequiredPFC = euroshinePlatinumRequired * PROVISION_FACTOR;

    // Disponible calculado con el requerido + provisión (Stock − Requerido PFC)
    const cordobaGoldDifferencePFC = parseFloat(cordobaGoldStock) - cordobaGoldRequiredPFC;
    const cordobaPlatinumDifferencePFC = parseFloat(cordobaPlatinumStock) - cordobaPlatinumRequiredPFC;
    const euroshineGoldDifferencePFC = indiaGoldTotal.pureWt - euroshineGoldRequiredPFC;
    const euroshinePlatinumDifferencePFC = indiaPlatinumTotal.pureWt - euroshinePlatinumRequiredPFC;
    const totalGoldDifferencePFC = cordobaGoldDifferencePFC + euroshineGoldDifferencePFC;
    const totalPlatinumDifferencePFC = cordobaPlatinumDifferencePFC + euroshinePlatinumDifferencePFC;

    const fmtG = (v) =>
      `${v.toLocaleString("es-ES", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}g`;

    const posColor = "#2e7d32";
    const negColor = "#c62828";
    const provColor = "#b26a00";
    const diffColor = (v) => (v >= 0 ? posColor : negColor);
    const DIVIDER = "rgba(224, 224, 224, 1)";

    const summaryTableSx = {
      width: "max-content",
      minWidth: "100%",
      "& .MuiTableCell-root": {
        borderBottom: `1px solid ${DIVIDER}`,
        borderLeft: "none",
        borderRight: "none",
        py: { xs: 0.4, md: 0.65 },
        px: { xs: 0.45, sm: 0.65, md: 1 },
        fontSize: { xs: "0.68rem", sm: "0.75rem", md: "0.8125rem" },
        lineHeight: 1.35,
      },
      "& .MuiTableHead-root .MuiTableCell-root": {
        color: "text.secondary",
        fontWeight: 500,
        fontSize: { xs: "0.62rem", sm: "0.68rem", md: "0.75rem" },
        borderBottom: `2px solid ${DIVIDER}`,
        backgroundColor: "#fff",
        whiteSpace: "nowrap",
        py: { xs: 0.45, md: 0.65 },
      },
      "& .summary-col-loc": {
        position: "sticky",
        left: 0,
        zIndex: 2,
        backgroundColor: "#fff",
        minWidth: { xs: "4.5rem", md: "5.5rem" },
        boxShadow: "2px 0 4px rgba(0,0,0,0.05)",
      },
      "& .MuiTableHead-root .summary-col-loc": {
        zIndex: 3,
      },
      "& .MuiTableBody-root .MuiTableRow-root:hover .summary-col-loc": {
        backgroundColor: "rgba(0, 0, 0, 0.04)",
      },
      "& .summary-group-head": {
        fontWeight: 600,
        fontSize: { xs: "0.65rem", md: "0.72rem" },
        letterSpacing: "0.02em",
        borderBottom: `1px solid ${DIVIDER}`,
      },
      "& .MuiTableBody-root .MuiTableRow-root:hover .MuiTableCell-root": {
        backgroundColor: "rgba(0, 0, 0, 0.04)",
      },
    };

    const numCell = (value, color, { isTotal = false } = {}, key) => (
      <TableCell
        key={key}
        align="right"
        sx={{
          fontVariantNumeric: "tabular-nums",
          fontWeight: isTotal ? 600 : 400,
          color,
          whiteSpace: "nowrap",
        }}
      >
        {fmtG(value)}
      </TableCell>
    );

    const colHead = (node, { tipText, align = "right" } = {}, key) => (
      <TableCell key={key} align={align}>
        {tipText ? (
          <Tooltip title={tipText} arrow>
            <Box component="span" sx={{ cursor: "help", borderBottom: "1px dotted", borderColor: "divider" }}>
              {node}
            </Box>
          </Tooltip>
        ) : (
          node
        )}
      </TableCell>
    );

    const renderMetalPriceChip = (label, eurPerGram, source, color) => {
      if (metalPrices.loading) {
        return (
          <Chip
            size="small"
            label={`${label}: …`}
            sx={{ fontSize: "0.7rem", height: 22 }}
          />
        );
      }

      if (!eurPerGram) {
        return (
          <Chip
            size="small"
            label={`${label}: N/D`}
            color="warning"
            variant="outlined"
            sx={{ fontSize: "0.7rem", height: 22 }}
          />
        );
      }

      return (
        <Tooltip
          title={`Fuente: ${source || "API"}${metalPrices.updatedAt ? ` · ${metalPrices.updatedAt.toLocaleString("es-ES")}` : ""}`}
          arrow
        >
          <Chip
            size="small"
            icon={
              <MaterialCommunityIcons
                name="gold"
                size={14}
                color={color}
                style={{ marginLeft: 6 }}
              />
            }
            label={`${label}: ${formatEuroPerGram(eurPerGram)}`}
            sx={{
              fontSize: "0.7rem",
              height: 22,
              fontFamily: "monospace",
              fontWeight: "bold",
              backgroundColor: "rgba(255,255,255,0.85)",
            }}
          />
        </Tooltip>
      );
    };

    const renderMetalPurchaseCostCell = (
      availableDiff,
      eurPerGram,
      metalLabel,
      isTotalRow = false,
      key
    ) => {
      const cost = calcSingleMetalPurchaseCost(availableDiff, eurPerGram);

      if (metalPrices.loading) {
        return (
          <TableCell key={key} align="right">
            <CircularProgress size={14} />
          </TableCell>
        );
      }

      const content = !cost.hasDeficit
        ? "—"
        : cost.cost != null
          ? formatEuroAmount(cost.cost)
          : "—";

      return (
        <TableCell
          key={key}
          align="right"
          sx={{
            fontVariantNumeric: "tabular-nums",
            fontWeight: isTotalRow ? 600 : 400,
            color: cost.hasDeficit
              ? cost.cost != null
                ? negColor
                : "warning.main"
              : "text.disabled",
            whiteSpace: "nowrap",
            fontSize: { xs: "0.66rem", md: "inherit" },
          }}
        >
          <Tooltip title={buildSingleMetalPurchaseTooltip(metalLabel, cost)} arrow>
            <Box component="span" sx={{ cursor: cost.hasDeficit ? "help" : "default" }}>
              {content}
              {cost.priceUnavailable ? "*" : ""}
            </Box>
          </Tooltip>
        </TableCell>
      );
    };

  // Datos por fila (Córdoba, Euroshine y TOTAL derivado)
  const summaryBaseRows = [
      {
        key: "cor",
        label: "Córdoba",
        goldStock: parseFloat(cordobaGoldStock),
        platStock: parseFloat(cordobaPlatinumStock),
        silverStock: 0,
        goldReq: cordobaGoldRequired,
        platReq: cordobaPlatinumRequired,
        silverReq: cordobaSilverRequired,
      },
      {
        key: "euro",
        label: "Euroshine",
        goldStock: indiaGoldTotal.pureWt,
        platStock: indiaPlatinumTotal.pureWt,
        silverStock: indiaSilverTotal.pureWt,
        goldReq: euroshineGoldRequired,
        platReq: euroshinePlatinumRequired,
        silverReq: euroshineSilverRequired,
      },
    ];
    const summaryTotalRow = {
      key: "tot",
      label: "TOTAL",
      isTotal: true,
      goldStock: summaryBaseRows[0].goldStock + summaryBaseRows[1].goldStock,
      platStock: summaryBaseRows[0].platStock + summaryBaseRows[1].platStock,
      silverStock: summaryBaseRows[0].silverStock + summaryBaseRows[1].silverStock,
      goldReq: summaryBaseRows[0].goldReq + summaryBaseRows[1].goldReq,
      platReq: summaryBaseRows[0].platReq + summaryBaseRows[1].platReq,
      silverReq: summaryBaseRows[0].silverReq + summaryBaseRows[1].silverReq,
    };
    const enrichRow = (r) => ({
      ...r,
      goldReqPFC: r.goldReq * PROVISION_FACTOR,
      platReqPFC: r.platReq * PROVISION_FACTOR,
      silverReqPFC: r.silverReq * PROVISION_FACTOR,
      goldDiff: r.goldStock - r.goldReq,
      platDiff: r.platStock - r.platReq,
      silverDiff: r.silverStock - r.silverReq,
      goldDiffPFC: r.goldStock - r.goldReq * PROVISION_FACTOR,
      platDiffPFC: r.platStock - r.platReq * PROVISION_FACTOR,
      silverDiffPFC: r.silverStock - r.silverReq * PROVISION_FACTOR,
    });
    const summaryRows = [...summaryBaseRows, summaryTotalRow].map(enrichRow);

  return (
      <Box sx={{ mb: 2, width: "100%", maxWidth: "100%", minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.875rem", lineHeight: 1.3 }}>
              Resumen de Metales
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
              Metal fino 999/925 · Disponible = Stock − Requerido
            </Typography>
            <Typography
              variant="caption"
              sx={{ display: { xs: "block", lg: "none" }, color: "text.secondary", fontSize: "0.68rem", mt: 0.25 }}
            >
              Desliza horizontalmente para ver todas las columnas →
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            {renderMetalPriceChip("Oro 999", metalPrices.goldEurPerGram, metalPrices.goldSource, "#D4AF37")}
            {renderMetalPriceChip("Platino 999", metalPrices.platinumEurPerGram, metalPrices.platinumSource, "#A8B4C0")}
            {renderMetalPriceChip("Plata 925", metalPrices.silverEurPerGram, metalPrices.silverSource, "#B0B7C0")}
          </Box>
        </Box>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: 1,
            width: "100%",
            maxWidth: "100%",
            overflowX: "auto",
            overflowY: "hidden",
            WebkitOverflowScrolling: "touch",
            border: `1px solid ${ESTADO_ORO_COLORS.SUMMARY_BORDER}`,
          }}
        >
          <Table size="small" sx={summaryTableSx}>
            <TableHead>
              <TableRow>
                <TableCell rowSpan={2} className="summary-col-loc" align="left">
                  Ubicación
                </TableCell>
                <TableCell colSpan={3} align="center" className="summary-group-head">
                  Stock
                </TableCell>
                <TableCell colSpan={6} align="center" className="summary-group-head">
                  Requerido
                </TableCell>
                <TableCell colSpan={6} align="center" className="summary-group-head">
                  Disponible
                </TableCell>
                <TableCell colSpan={6} align="center" className="summary-group-head">
                  Compra
                </TableCell>
              </TableRow>
              <TableRow>
                {colHead("Oro", {}, "h-gs")}
                {colHead("Plat.", {}, "h-ps")}
                {colHead("Plata", { tipText: "Solo Euroshine (hoja Silver Stock)" }, "h-ss")}
                {colHead("Oro", {}, "h-gr")}
                {colHead("Oro PFC", { tipText: "Requerido + 28% provisión fundición" }, "h-grp")}
                {colHead("Plat.", {}, "h-pr")}
                {colHead("Plat. PFC", { tipText: "Requerido + 28% provisión fundición" }, "h-prp")}
                {colHead("Plata", {}, "h-sr")}
                {colHead("Plata PFC", { tipText: "Requerido + 28% provisión fundición" }, "h-srp")}
                {colHead("Oro", {}, "h-gd")}
                {colHead("Oro PFC", { tipText: "Stock − Oro req. PFC" }, "h-gdp")}
                {colHead("Plat.", {}, "h-pd")}
                {colHead("Plat. PFC", { tipText: "Stock − Plat. req. PFC" }, "h-pdp")}
                {colHead("Plata", {}, "h-sd")}
                {colHead("Plata PFC", { tipText: "Stock − Plata req. PFC" }, "h-sdp")}
                {colHead("Oro", {}, "h-cg")}
                {colHead("Oro PFC", { tipText: "Déficit oro PFC × precio" }, "h-cgp")}
                {colHead("Plat.", {}, "h-cp")}
                {colHead("Plat. PFC", { tipText: "Déficit platino PFC × precio" }, "h-cpp")}
                {colHead("Plata", {}, "h-cs")}
                {colHead("Plata PFC", { tipText: "Déficit plata PFC × precio" }, "h-csp")}
              </TableRow>
            </TableHead>
              <TableBody>
                {summaryRows.map((r) => (
                  <TableRow key={r.key}>
                    <TableCell
                      className="summary-col-loc"
                      align="left"
                      sx={{
                        fontWeight: r.isTotal ? 600 : 500,
                        color: "text.primary",
                      }}
                    >
                      {r.label}
                    </TableCell>
                    {numCell(r.goldStock, posColor, { isTotal: r.isTotal }, `${r.key}-gs`)}
                    {numCell(r.platStock, posColor, { isTotal: r.isTotal }, `${r.key}-ps`)}
                    {numCell(r.silverStock, posColor, { isTotal: r.isTotal }, `${r.key}-ss`)}
                    {numCell(r.goldReq, negColor, { isTotal: r.isTotal }, `${r.key}-gr`)}
                    {numCell(r.goldReqPFC, provColor, { isTotal: r.isTotal }, `${r.key}-grp`)}
                    {numCell(r.platReq, negColor, { isTotal: r.isTotal }, `${r.key}-pr`)}
                    {numCell(r.platReqPFC, provColor, { isTotal: r.isTotal }, `${r.key}-prp`)}
                    {numCell(r.silverReq, negColor, { isTotal: r.isTotal }, `${r.key}-sr`)}
                    {numCell(r.silverReqPFC, provColor, { isTotal: r.isTotal }, `${r.key}-srp`)}
                    {numCell(r.goldDiff, diffColor(r.goldDiff), { isTotal: r.isTotal }, `${r.key}-gd`)}
                    {numCell(r.goldDiffPFC, diffColor(r.goldDiffPFC), { isTotal: r.isTotal }, `${r.key}-gdp`)}
                    {numCell(r.platDiff, diffColor(r.platDiff), { isTotal: r.isTotal }, `${r.key}-pd`)}
                    {numCell(r.platDiffPFC, diffColor(r.platDiffPFC), { isTotal: r.isTotal }, `${r.key}-pdp`)}
                    {numCell(r.silverDiff, diffColor(r.silverDiff), { isTotal: r.isTotal }, `${r.key}-sd`)}
                    {numCell(r.silverDiffPFC, diffColor(r.silverDiffPFC), { isTotal: r.isTotal }, `${r.key}-sdp`)}
                    {renderMetalPurchaseCostCell(r.goldDiff, metalPrices.goldEurPerGram, "Oro", r.isTotal, `${r.key}-cg`)}
                    {renderMetalPurchaseCostCell(r.goldDiffPFC, metalPrices.goldEurPerGram, "Oro (PFC)", r.isTotal, `${r.key}-cgp`)}
                    {renderMetalPurchaseCostCell(r.platDiff, metalPrices.platinumEurPerGram, "Platino", r.isTotal, `${r.key}-cp`)}
                    {renderMetalPurchaseCostCell(r.platDiffPFC, metalPrices.platinumEurPerGram, "Platino (PFC)", r.isTotal, `${r.key}-cpp`)}
                    {renderMetalPurchaseCostCell(r.silverDiff, metalPrices.silverEurPerGram, "Plata", r.isTotal, `${r.key}-cs`)}
                    {renderMetalPurchaseCostCell(r.silverDiffPFC, metalPrices.silverEurPerGram, "Plata (PFC)", r.isTotal, `${r.key}-csp`)}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
    );
  };

  // Mapeo seguro de detalles (excelDetailsData) a estructura para el calendario
  const mapExcelToCalendarDetails = (detailsArray) => {
    if (!Array.isArray(detailsArray)) return [];
    return detailsArray.map((item) => {
      const fechaRaw = item.fecha || item.date || item.Fecha || item.FECHA || item.orderDate || (item.fechaentrega && (item.fechaentrega.date || item.fechaentrega));
      const fecha = fechaRaw instanceof Date ? fechaRaw : new Date(fechaRaw);
      const goldReq =
        parseFloat(
          item.goldRequired ?? item.oroRequired ?? item.oro999Required ?? item.oro ?? item.Oro ?? 0
        ) || 0;
      const platReq =
        parseFloat(
          item.platinumRequired ?? item.platinoRequired ?? item.platino999Required ?? item.platino ?? item.Platino ?? 0
        ) || 0;
      const locRaw = (item.location || item.Loc_Desc || item.ubicacion || item.Ubicacion || "Cordoba");
      return {
        fecha,
        goldRequired: goldReq,
        platinumRequired: platReq,
        location: String(locRaw).toLowerCase().includes("euro") ? "Euroshine" : "Cordoba",
      };
    });
  };

  // Totales de stock fino (BD Córdoba + Euroshine)
  const stockTotalOro =
    parseFloat(cordobaData.stock?.find((i) => i.metal === "ORO")?.stock || 0) +
    calculateTotals(indiaData.goldStock || [], "gold").pureWt;
  const stockTotalPlatino =
    parseFloat(cordobaData.stock?.find((i) => i.metal === "PLATINO")?.stock || 0) +
    calculateTotals(indiaData.platinumStock || [], "platinum").pureWt;
  const stockTotalPlata = calculateTotals(indiaData.silverStock || [], "gold").pureWt;

  const coverage =
    excelDetailsData.length > 0
      ? calculateMetalCoverage(stockTotalOro, stockTotalPlatino, excelDetailsData)
      : null;

  const formatDateShort = (date) =>
    date
      ? date.toLocaleDateString("es-ES", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "";

  // Banner explicativo: de dónde vienen los datos y cómo se calcula todo
  const renderHelpBanner = () => (
    <Box
      sx={{
        mb: 2,
        borderRadius: 1,
        border: `1px solid ${ESTADO_ORO_COLORS.SUMMARY_BORDER}`,
        backgroundColor: ESTADO_ORO_COLORS.SUMMARY_BG,
        overflow: "hidden",
      }}
    >
      <Box
        onClick={() => setShowHelp((v) => !v)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1,
          cursor: "pointer",
          "&:hover": { backgroundColor: "rgba(0,0,0,0.03)" },
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 18, color: ESTADO_ORO_COLORS.SUMMARY_ICON }} />
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: "bold", fontSize: "0.8rem", color: ESTADO_ORO_COLORS.SUMMARY_TEXT }}
        >
          ¿Cómo se calcula esta pantalla?
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", ml: 0.5 }}>
          Última actualización: {lastUpdate}
        </Typography>
        <Box sx={{ ml: "auto" }}>
          {showHelp ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
      </Box>
      <Collapse in={showHelp}>
        <Divider />
        <Box sx={{ p: 1.5 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            divider={<Divider orientation="vertical" flexItem />}
          >
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                <StorageIcon sx={{ fontSize: 16, color: "success.main" }} />
                <Typography variant="caption" sx={{ fontWeight: "bold", fontSize: "0.72rem" }}>
                  1. Stock (Córdoba y Euroshine)
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.68rem", lineHeight: 1.4 }}>
                El stock de <strong>Córdoba</strong> se lee de la base de datos SQL Server. El de{" "}
                <strong>Euroshine</strong> viene de un Excel en el servidor. Todo expresado en metal fino
                (999). Ver detalle de orígenes abajo.
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                <DescriptionIcon sx={{ fontSize: 16, color: "primary.main" }} />
                <Typography variant="caption" sx={{ fontWeight: "bold", fontSize: "0.72rem" }}>
                  2. Pedidos (Excel nocturno)
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.68rem", lineHeight: 1.4 }}>
                Cada noche llegan los Excel con las órdenes. Los gramos de cada quilataje (9/14/18/19 kt,
                platino) se convierten a metal fino según su pureza y se reparten por fábrica: FJS →
                Córdoba, ES → Euroshine.
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                <CalculateIcon sx={{ fontSize: 16, color: "error.main" }} />
                <Typography variant="caption" sx={{ fontWeight: "bold", fontSize: "0.72rem" }}>
                  3. Disponible y cobertura
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.68rem", lineHeight: 1.4 }}>
                Disponible = Stock − Requerido. El calendario acumula los pedidos por fecha de entrega
                para saber hasta qué día aguanta el stock (los días en rojo indican déficit).
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ my: 1.5 }} />

          <Typography variant="caption" sx={{ display: "block", fontWeight: "bold", fontSize: "0.72rem", color: ESTADO_ORO_COLORS.SUMMARY_TEXT, mb: 0.5 }}>
            Origen de los datos de stock
          </Typography>
          <TableContainer component={Paper} sx={{ boxShadow: "none", border: `1px solid ${ESTADO_ORO_COLORS.SUMMARY_BORDER}` }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Ubicación", "Oro 999 Stock", "Platino 999 Stock", "Plata 925 Stock", "Origen"].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontWeight: "bold",
                        fontSize: "0.66rem",
                        backgroundColor: ESTADO_ORO_COLORS.ICON_PRIMARY,
                        color: ESTADO_ORO_COLORS.TABLE_HEADER_TEXT,
                        py: 0.5,
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  {
                    loc: "Córdoba",
                    oro: "Campo stock donde metal = 'ORO'",
                    platino: "Campo stock donde metal = 'PLATINO'",
                    plata: "—",
                    origen: "Base de datos SQL Server → tabla stock_metal_fjs",
                    color: "#4caf50",
                  },
                  {
                    loc: "Euroshine",
                    oro: "Suma de Pure_Wt de la hoja Gold Stock",
                    platino: "Suma de Pure_Wt de la hoja Platinum Stock",
                    plata: "Suma de Pure_Wt de la hoja Silver Stock",
                    origen: "Excel en el servidor",
                    color: "#2196f3",
                  },
                  {
                    loc: "TOTAL",
                    oro: "Córdoba + Euroshine",
                    platino: "Córdoba + Euroshine",
                    plata: "Solo Euroshine (Excel)",
                    origen: "Calculado en el frontend",
                    color: "#9e9e9e",
                  },
                ].map((row, i) => (
                  <TableRow key={row.loc} sx={{ backgroundColor: row.loc === "TOTAL" ? ESTADO_ORO_COLORS.TOTAL_ROW_BG : i % 2 === 0 ? "#ffffff" : "#f7f9fa" }}>
                    <TableCell sx={{ borderLeft: `3px solid ${row.color}`, fontWeight: "bold", fontSize: "0.66rem", py: 0.5 }}>
                      {row.loc}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.66rem", color: "text.secondary", py: 0.5 }}>{row.oro}</TableCell>
                    <TableCell sx={{ fontSize: "0.66rem", color: "text.secondary", py: 0.5 }}>{row.platino}</TableCell>
                    <TableCell sx={{ fontSize: "0.66rem", color: "text.secondary", py: 0.5 }}>{row.plata || "—"}</TableCell>
                    <TableCell sx={{ fontSize: "0.66rem", py: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {row.loc === "Córdoba" ? (
                          <StorageIcon sx={{ fontSize: 13, color: "success.main" }} />
                        ) : row.loc === "Euroshine" ? (
                          <DescriptionIcon sx={{ fontSize: 13, color: "primary.main" }} />
                        ) : (
                          <CalculateIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                        )}
                        <span>{row.origen}</span>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Collapse>
    </Box>
  );

  // Tarjeta KPI: cuánto dura el stock de un metal
  const renderCoverageCard = (label, metalCoverage, accentColor) => {
    if (!metalCoverage) return null;
    const covered = metalCoverage.covered;
    const Icon = covered ? EventAvailableIcon : EventBusyIcon;
    const statusColor = covered ? "success.main" : "error.main";

    return (
      <Box
        sx={{
          flex: 1,
          minWidth: 220,
          p: 1.5,
          borderRadius: 1,
          backgroundColor: "#ffffff",
          border: `1px solid ${ESTADO_ORO_COLORS.SUMMARY_BORDER}`,
          borderLeft: `4px solid ${accentColor}`,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Icon sx={{ fontSize: 18, color: statusColor }} />
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", fontSize: "0.78rem" }}>
            {label}
          </Typography>
        </Box>

        {covered ? (
          <>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: statusColor, fontSize: "1.05rem", lineHeight: 1.1 }}>
              Stock suficiente
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.68rem" }}>
              Cubre todos los pedidos cargados. Sobran{" "}
              <strong>
                {metalCoverage.remaining.toLocaleString("es-ES", {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })}
                g
              </strong>{" "}
              de fino.
            </Typography>
          </>
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
              <Typography variant="h5" sx={{ fontWeight: "bold", color: statusColor, fontSize: "1.4rem", lineHeight: 1 }}>
                {metalCoverage.days}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
                {metalCoverage.days === 1 ? "día" : "días"} de margen
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.68rem" }}>
              El stock se agota el <strong>{formatDateShort(metalCoverage.runoutDate)}</strong> según los
              pedidos cargados.
            </Typography>
          </>
        )}
      </Box>
    );
  };

  // Sección de cobertura + leyenda del calendario
  const renderCoverageSection = () => {
    if (!coverage) return null;
    return (
      <Box
        sx={{
          mb: 2,
          p: 1.5,
          borderRadius: 1,
          backgroundColor: ESTADO_ORO_COLORS.SUMMARY_BG,
          border: `1px solid ${ESTADO_ORO_COLORS.SUMMARY_BORDER}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <MaterialCommunityIcons name="calendar-clock" size={18} color={ESTADO_ORO_COLORS.SUMMARY_ICON} />
          <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.8rem", color: ESTADO_ORO_COLORS.SUMMARY_TEXT }}>
            Cobertura del stock
          </Typography>
          <Tooltip
            title="Días hasta que el stock actual no pueda cubrir los pedidos acumulados por su fecha de entrega. Coincide con el día que el calendario se pone en rojo."
            arrow
          >
            <InfoOutlinedIcon sx={{ fontSize: 15, color: "text.secondary", cursor: "help" }} />
          </Tooltip>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 1.5 }}>
          {renderCoverageCard("Oro 999", coverage.gold, "#D4AF37")}
          {renderCoverageCard("Platino 999", coverage.platinum, "#A8B4C0")}
        </Box>

        {/* Leyenda del calendario */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Typography variant="caption" sx={{ fontWeight: "bold", fontSize: "0.68rem", color: "text.secondary" }}>
            Calendario:
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: 0.5, backgroundColor: "#d4edda", border: "1px solid #b7dfc0" }} />
            <Typography variant="caption" sx={{ fontSize: "0.66rem", color: "text.secondary" }}>
              Stock cubre el día
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: 0.5, backgroundColor: "#f8d7da", border: "1px solid #f0b6bc" }} />
            <Typography variant="caption" sx={{ fontSize: "0.66rem", color: "text.secondary" }}>
              Déficit (falta metal)
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ fontSize: "0.66rem", color: "text.secondary", fontStyle: "italic" }}>
            Haz clic en un día para ver el detalle de stock, requerido y disponible.
          </Typography>
        </Box>
      </Box>
    );
  };

  const formatHorizonDate = (date) =>
    date
      ? date.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" })
      : "";

  const METAL_LABEL = {
    oro: "Oro",
    platino: "Platino",
    plata: "Plata",
    titanio: "Titanio",
  };

  const METAL_SPHERE = {
    oro: {
      gradient: "linear-gradient(145deg, #fff0b3 0%, #e8c547 35%, #c9a227 70%, #8b6914 100%)",
      highlight: "rgba(255,255,255,0.55)",
      shadow: "rgba(80,50,0,0.35)",
    },
    platino: {
      gradient: "linear-gradient(145deg, #f0f4f8 0%, #c8d0da 40%, #9aa8b8 75%, #6b7a8a 100%)",
      highlight: "rgba(255,255,255,0.7)",
      shadow: "rgba(40,50,60,0.3)",
    },
    plata: {
      gradient: "linear-gradient(145deg, #f5f6f7 0%, #d0d5db 40%, #a8afb8 75%, #7a828a 100%)",
      highlight: "rgba(255,255,255,0.65)",
      shadow: "rgba(50,55,60,0.28)",
    },
    titanio: {
      gradient: "linear-gradient(145deg, #b8c0c8 0%, #8a939e 40%, #5c6570 75%, #3d4550 100%)",
      highlight: "rgba(255,255,255,0.35)",
      shadow: "rgba(20,25,30,0.4)",
    },
  };

  const renderMetalSphere = (metalKey) => {
    const cfg = METAL_SPHERE[metalKey] || METAL_SPHERE.titanio;
    return (
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          flexShrink: 0,
          background: cfg.gradient,
          boxShadow: `inset -2px -3px 5px ${cfg.shadow}, inset 2px 2px 4px ${cfg.highlight}, 0 1px 2px rgba(0,0,0,0.12)`,
        }}
      />
    );
  };

  const HORIZON_UI = {
    border: ESTADO_ORO_COLORS.SUMMARY_BORDER,
    cardBarBg: "#ffffff",
    softBlue: "#395c60",
    headerText: "#ffffff",
    headerTextMuted: "rgba(255,255,255,0.78)",
    brand: ESTADO_ORO_COLORS.SUMMARY_TEXT,
    brandSoft: BRAND_COLORS.PRIMARY_LIGHT,
    okBg: "#f0fdf4",
    okText: "#166534",
    warnBg: "#fff7ed",
    warnText: "#c2410c",
    errBg: "#fef2f2",
    errText: "#991b1b",
  };

  const TB = {
    font: "0.8125rem",
    fontSm: "0.75rem",
    text: ESTADO_ORO_COLORS.SUMMARY_TEXT,
    muted: TEXT_COLORS.MUTED,
    divider: ESTADO_ORO_COLORS.TABLE_BORDER,
    dot: { ok: HORIZON_UI.okText, warn: HORIZON_UI.warnText, err: HORIZON_UI.errText, muted: "#94a3b8" },
  };

  const STOCK_COMPARE_KEYS = new Set(["oro", "platino", "plata"]);
  const HORIZON_SUB_COLS = 3;

  const horizonEmptyCell = () => (
    <Typography variant="body2" sx={{ color: TB.muted, fontSize: TB.font, textAlign: "center" }}>
      —
    </Typography>
  );

  /** Valores de una celda (gramos + €) */
  const horizonSubCellValues = (gramsStr, importeStr, { error = false } = {}) => (
    <Box sx={{ minWidth: 0, lineHeight: 1.25, textAlign: "center" }}>
      <Box
        component="div"
        sx={{
          fontSize: TB.font,
          fontWeight: 500,
          fontVariantNumeric: "tabular-nums",
          color: error ? HORIZON_UI.errText : HORIZON_UI.brand,
          lineHeight: 1.25,
          overflowWrap: "anywhere",
        }}
      >
        {gramsStr}
      </Box>
      <Box
        component="div"
        sx={{
          fontSize: TB.fontSm,
          fontVariantNumeric: "tabular-nums",
          color: error ? HORIZON_UI.errText : TB.muted,
          lineHeight: 1.2,
          mt: "2px",
          overflowWrap: "anywhere",
        }}
      >
        {importeStr}
      </Box>
    </Box>
  );

  const getHorizonBucketMetrics = (
    cell,
    { eurPerGram, metalKey, cumulativeGrams, cumulativeGramsBefore }
  ) => {
    const grams = cell?.grams || 0;
    const orders = cell?.orders || 0;
    const amount = eurPerGram != null ? grams * eurPerGram : null;
    const showStockCompare = metalKey && STOCK_COMPARE_KEYS.has(metalKey);
    const totalStock =
      metalKey === "oro"
        ? stockTotalOro
        : metalKey === "platino"
          ? stockTotalPlatino
          : metalKey === "plata"
            ? stockTotalPlata
            : 0;
    const cumNeed = cumulativeGrams != null ? cumulativeGrams : grams;
    const stockRemaining = showStockCompare
      ? Math.max(0, totalStock - cumulativeGramsBefore)
      : 0;
    const falta = showStockCompare ? Math.max(0, cumNeed - totalStock) : 0;
    const empty = grams === 0 && orders === 0;

    const amountStr = amount != null ? formatEuroAmount(amount) : "—";
    const dispEur =
      showStockCompare && eurPerGram != null && stockRemaining > 0
        ? formatEuroAmount(stockRemaining * eurPerGram)
        : "—";
    const pendEur =
      showStockCompare && eurPerGram != null && falta > 0
        ? formatEuroAmount(falta * eurPerGram)
        : "—";

    let statusWord = "—";
    let statusColor = TB.dot.muted;
    if (!empty) {
      if (showStockCompare) {
        statusWord = falta > 0 ? "Pendiente" : cumNeed > 0 || grams > 0 ? "Cubierto" : "—";
        statusColor =
          falta > 0 ? TB.dot.err : cumNeed > 0 || grams > 0 ? TB.dot.ok : TB.dot.muted;
      } else {
        statusWord = amount == null ? "Sin precio" : "OK";
        statusColor = amount == null ? TB.dot.warn : TB.dot.ok;
      }
    }

    return {
      empty,
      showStockCompare,
      grams,
      orders,
      amountStr,
      stockRemaining,
      dispEur,
      falta,
      pendEur,
      statusWord,
      statusColor,
      cumNeed,
    };
  };

  const horizonStatusLine = (word, color, orders, { onOrdersClick } = {}) => {
    const isErr = color === TB.dot.err;
    const isOk = color === TB.dot.ok;
    const isWarn = color === TB.dot.warn;
    const canOpenModal = orders > 0 && typeof onOrdersClick === "function" && word !== "—";
    const pillSx = isErr
      ? { bgcolor: HORIZON_UI.errBg, color: HORIZON_UI.errText }
      : isOk
        ? { bgcolor: HORIZON_UI.okBg, color: HORIZON_UI.okText }
        : isWarn
          ? { bgcolor: HORIZON_UI.warnBg, color: HORIZON_UI.warnText }
          : null;

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 0.75,
          width: "100%",
          fontSize: TB.fontSm,
          lineHeight: 1.2,
        }}
      >
        {pillSx ? (
          <Box
            component="span"
            role={canOpenModal ? "button" : undefined}
            tabIndex={canOpenModal ? 0 : undefined}
            onClick={canOpenModal ? onOrdersClick : undefined}
            onKeyDown={
              canOpenModal
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onOrdersClick();
                    }
                  }
                : undefined
            }
            sx={{
              ...pillSx,
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              px: 0.75,
              py: 0.25,
              borderRadius: "4px",
              fontWeight: 600,
              cursor: canOpenModal ? "pointer" : "default",
              ...(canOpenModal && {
                "&:hover": { filter: "brightness(0.97)", boxShadow: "0 0 0 1px currentColor" },
              }),
            }}
          >
            {word}
            {isOk && " ✓"}
            <Box component="span" sx={{ fontWeight: 400, opacity: 0.85 }}>· {orders} ped.</Box>
          </Box>
        ) : (
          <>
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "3px", color: TB.muted }}>
              <Box component="span" sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
              {word}
            </Box>
            <Box component="span" sx={{ color: TB.muted, fontVariantNumeric: "tabular-nums" }}>{orders} ped.</Box>
          </>
        )}
      </Box>
    );
  };

  const isMoOrderCode = (orden) => /^[48]/.test(String(orden || "").trim());

  const openHorizonBucketOrdersModal = (metalRow, bucket, metrics) => {
    const cell = metalRow.buckets[bucket.key];
    if (!cell?.orderItems?.length) return;
    setHorizonOrdersModal({
      metalKey: metalRow.key,
      metal: METAL_LABEL[metalRow.key] || metalRow.label,
      bucketLabel: bucket.label,
      bucketDateRange: bucket.dateRangeLabel,
      statusWord: metrics.statusWord,
      orders: cell.orderItems,
    });
  };

  const renderHorizonOrdersModal = () => {
    if (!horizonOrdersModal) return null;

    const formatOrderDeliveryDate = (order) => {
      const d = order.deliveryDate
        ? new Date(order.deliveryDate)
        : excelSerialToDate(order.fecha_entrega);
      return d && !isNaN(d.getTime()) ? formatHorizonDate(d) : "—";
    };

    return (
      <Dialog
        open
        onClose={() => setHorizonOrdersModal(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            bgcolor: BACKGROUND_COLORS.SECONDARY,
            color: ESTADO_ORO_COLORS.SUMMARY_TEXT,
            fontSize: "0.95rem",
            fontWeight: 700,
            py: 1.5,
          }}
        >
          Pedidos · {horizonOrdersModal.metal}
          <Typography component="div" variant="caption" sx={{ display: "block", fontWeight: 500, color: TEXT_COLORS.MUTED, mt: 0.5 }}>
            {horizonOrdersModal.bucketLabel}
            {horizonOrdersModal.bucketDateRange ? ` (${horizonOrdersModal.bucketDateRange})` : ""}
            {" · "}
            {horizonOrdersModal.statusWord}
            {" · "}
            {horizonOrdersModal.orders.length} ped.
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ maxHeight: 420, overflowY: "auto", p: 1.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
            {horizonOrdersModal.orders.map((order, idx) => {
              const ordenValue = order.orden || "";
              const clickable = isMoOrderCode(ordenValue);
              return (
                <Box
                  key={`${ordenValue}-${idx}`}
                  onClick={clickable ? () => handleOrderClick(ordenValue) : undefined}
                  sx={{
                    border: `1px solid ${ESTADO_ORO_COLORS.SUMMARY_BORDER}`,
                    borderRadius: 1,
                    p: 1,
                    bgcolor: "#fff",
                    cursor: clickable ? "pointer" : "default",
                    transition: "background-color 0.12s ease, border-color 0.12s ease",
                    ...(clickable && {
                      "&:hover": { backgroundColor: "#eef4ff", borderColor: "#b6ccf5" },
                    }),
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.25 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.8rem",
                        color: clickable ? "#1565c0" : ESTADO_ORO_COLORS.SUMMARY_TEXT,
                        textDecoration: clickable ? "underline" : "none",
                      }}
                    >
                      {ordenValue || "—"}
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary", whiteSpace: "nowrap" }}>
                      {parseFloat(order.gramos || 0).toFixed(1)} g
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.25 }}>
                    <Typography variant="caption" sx={{ color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {order.cliente || order.articulo || "—"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#8a6d3b", whiteSpace: "nowrap" }}>
                      {order.metal || ""}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Entrega: {formatOrderDeliveryDate(order)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: ESTADO_ORO_COLORS.SUMMARY_TEXT, fontWeight: 600 }}>
                      Fino PFC: {fmtNum(order.fineGrams || 0)} g
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.25, bgcolor: BACKGROUND_COLORS.SECONDARY }}>
          <Button onClick={() => setHorizonOrdersModal(null)} variant="outlined" size="small">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderHorizonPricesInline = () => {
    const items = [
      { label: "Oro", v: metalPrices.goldEurPerGram, src: metalPrices.goldSource },
      { label: "Platino", v: metalPrices.platinumEurPerGram, src: metalPrices.platinumSource },
      { label: "Plata", v: metalPrices.silverEurPerGram, src: metalPrices.silverSource },
      { label: "Titanio", v: metalPrices.titaniumEurPerGram, src: metalPrices.titaniumSource },
    ];
    if (metalPrices.loading) {
      return <Box sx={{ fontSize: TB.fontSm, color: TB.muted }}>Precios: cargando…</Box>;
    }
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 1 }}>
        {items.map((item) => (
          <Tooltip
            key={item.label}
            title={item.src ? `Fuente: ${item.src}` : ""}
            disableHoverListener={!item.src}
          >
            <Box
              component="span"
              sx={{
                border: `1px solid ${HORIZON_UI.border}`,
                borderRadius: "6px",
                px: 1.25,
                py: 0.5,
                bgcolor: "#fff",
                whiteSpace: "nowrap",
                fontSize: TB.fontSm,
                fontWeight: 500,
              }}
            >
              <Box component="span" sx={{ color: TB.muted, mr: 0.5 }}>{item.label}</Box>
              <Box
                component="span"
                sx={{
                  color: HORIZON_UI.brand,
                  fontWeight: 600,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {item.v ? formatEuroPerGram(item.v) : "N/D"}
              </Box>
            </Box>
          </Tooltip>
        ))}
      </Box>
    );
  };

  const renderMetalNeedHorizons = () => {
    if (excelDetailsData.length === 0) return null;

    const { metals, referenceDate, horizonEnd, bucketDefs, excludedPeriods } = metalNeedHorizons;
    const horizonLabel = formatHorizonDate(horizonEnd);

    const metalEurPerGram = {
      oro: metalPrices.goldEurPerGram,
      platino: metalPrices.platinumEurPerGram,
      plata: metalPrices.silverEurPerGram,
      titanio: metalPrices.titaniumEurPerGram,
    };
    const getMetalEurPerGram = (metalKey) => metalEurPerGram[metalKey] ?? null;

    const totalDataCols = bucketDefs.length * HORIZON_SUB_COLS;
    const dataColWidth = `calc((100% - 4.75rem) / ${totalDataCols})`;
    const periodDividerClass = (bucketIdx) =>
      bucketIdx < bucketDefs.length - 1 ? "horizon-period-divider" : "";

    const horizonTableSx = {
      width: "100%",
      tableLayout: "fixed",
      "& .MuiTableCell-root": {
        borderBottom: `1px solid ${HORIZON_UI.border}`,
        borderLeft: "none",
        borderRight: "none",
        py: 0.65,
        px: 0.75,
        verticalAlign: "middle",
        textAlign: "center",
        overflow: "hidden",
        backgroundColor: "#ffffff",
      },
      "& .horizon-col-metal": {
        width: "4.75rem",
        maxWidth: "4.75rem",
        px: 1,
        borderRight: `1px solid rgba(255,255,255,0.2)`,
        backgroundColor: HORIZON_UI.softBlue,
        color: HORIZON_UI.headerText,
      },
      "& .horizon-data-col": {
        width: dataColWidth,
        maxWidth: dataColWidth,
      },
      "& .horizon-period-divider": {
        borderRight: `2px solid ${HORIZON_UI.border}`,
      },
      "& .MuiTableHead-root .horizon-period-divider": {
        borderRight: `2px solid rgba(255,255,255,0.35)`,
      },
      "& .MuiTableHead-root .MuiTableCell-root": {
        backgroundColor: HORIZON_UI.softBlue,
        color: HORIZON_UI.headerText,
        fontWeight: 600,
        fontSize: "0.6875rem",
        textTransform: "none",
        letterSpacing: 0,
        borderBottom: `1px solid rgba(255,255,255,0.2)`,
        py: 0.75,
        lineHeight: 1.25,
        overflowWrap: "anywhere",
      },
      "& .MuiTableHead-root .horizon-col-metal": {
        textTransform: "none",
        letterSpacing: 0,
        fontSize: TB.fontSm,
        verticalAlign: "middle",
      },
      "& .MuiTableHead-root .MuiTableRow:first-of-type .MuiTableCell-root": {
        textTransform: "none",
        letterSpacing: 0,
        fontSize: TB.font,
        color: HORIZON_UI.headerText,
        borderBottom: `1px solid rgba(255,255,255,0.2)`,
      },
      "& .MuiTableHead-root .MuiTableRow:first-of-type .horizon-col-metal": {
        fontSize: TB.fontSm,
      },
      "& .MuiTableHead-root .MuiTableRow:last-of-type .MuiTableCell-root": {
        borderBottom: `2px solid rgba(255,255,255,0.35)`,
        py: 0.5,
      },
      "& .MuiTableBody-root .MuiTableRow-root:hover .horizon-data-col": {
        backgroundColor: BACKGROUND_COLORS.PRIMARY,
      },
    };

    return (
      <Box
        sx={{
          mb: 2,
          border: `1px solid ${HORIZON_UI.border}`,
          borderRadius: 2,
          bgcolor: "#fff",
          boxShadow: "0 1px 4px rgba(23, 64, 69, 0.06)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${HORIZON_UI.border}`,
            bgcolor: HORIZON_UI.cardBarBg,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              minWidth: 0,
              flex: "1 1 auto",
            }}
          >
            <CalculateIcon sx={{ fontSize: 18, color: HORIZON_UI.brand, flexShrink: 0 }} />
            <Typography
              component="span"
              variant="subtitle2"
              sx={{ fontWeight: 600, fontSize: "0.875rem", lineHeight: 1.3, whiteSpace: "nowrap", color: HORIZON_UI.brand }}
            >
              Previsión de necesidad de metal{" "}
              <Box component="span" sx={{ color: TB.muted, fontWeight: 500 }}>
                (PFC +28 %)
              </Box>
            </Typography>
            <Typography
              component="span"
              variant="caption"
              sx={{ color: TB.muted, fontSize: "0.75rem", lineHeight: 1.3 }}
            >
              Horizonte hasta {horizonLabel} · ref. {formatHorizonDate(referenceDate)}
              {excludedPeriods.length > 0 && ` · ${excludedPeriods.length} periodo(s) excluido(s)`}
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => setNeedHorizonHelpAnchor(e.currentTarget)}
              sx={{ color: HORIZON_UI.brandSoft, p: 0.25, ml: -0.25 }}
              aria-label="Cómo funciona la previsión de necesidad"
            >
              <ReportProblemIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <Box sx={{ flexShrink: 0, ml: "auto" }}>{renderHorizonPricesInline()}</Box>
        </Box>

        <Popover
          open={Boolean(needHorizonHelpAnchor)}
          anchorEl={needHorizonHelpAnchor}
          onClose={() => setNeedHorizonHelpAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{ sx: { maxWidth: 420, p: 2 } }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
            ¿Cómo funciona esta previsión?
          </Typography>
          <Stack spacing={1}>
            <Typography variant="caption" sx={{ fontSize: "0.72rem", lineHeight: 1.5, color: "text.secondary" }}>
              Para poder entregar un pedido en su <strong>fecha de entrega</strong>, el metal debe estar
              disponible <strong>6 semanas (42 días efectivos) antes</strong>. Los días de vacaciones o
              festivos configurados en <code>MetalNeedHorizonConfig.js</code> no cuentan en ese plazo.
            </Typography>
            <Typography variant="caption" sx={{ fontSize: "0.72rem", lineHeight: 1.5, color: "text.secondary" }}>
              <strong>Necesidad actual:</strong> pedidos <strong>retrasados</strong> y entregas hasta el fin
              de la quincena en curso (días 1–15 o 16–fin de mes). En oro, platino y plata, cada tramo muestra
              necesidad del periodo · stock <em>restante</em> (total − necesidad acumulada anterior) · déficit
              acumulado (necesidad acumulada − stock total).
            </Typography>
            <Typography variant="caption" sx={{ fontSize: "0.72rem", lineHeight: 1.5, color: "text.secondary" }}>
              El stock total es único para todos los tramos: si en «Necesidad actual» ya hay déficit,
              los tramos siguientes muestran 0 g de stock disponible hasta que se compre más metal.
            </Typography>
            <Typography variant="caption" sx={{ fontSize: "0.72rem", lineHeight: 1.5, color: "text.secondary" }}>
              <strong>Quincenas siguientes:</strong> cada columna agrupa pedidos por fecha de entrega
              (1ª o 2ª quincena del mes). Si una quincena entera cae en vacaciones, no se muestra columna.
              Una quincena con días laborables y festivos muestra el rango completo del calendario.
            </Typography>
            {excludedPeriods.length > 0 && (
              <Box>
                <Typography variant="caption" sx={{ fontSize: "0.72rem", fontWeight: "bold", color: "text.secondary" }}>
                  Periodos excluidos actualmente:
                </Typography>
                {excludedPeriods.map((p) => (
                  <Typography key={p.id} variant="caption" sx={{ display: "block", fontSize: "0.7rem", color: "text.secondary" }}>
                    · {p.label}: {p.from} → {p.to}
                  </Typography>
                ))}
              </Box>
            )}
            <Typography variant="caption" sx={{ fontSize: "0.72rem", lineHeight: 1.5, color: "text.secondary" }}>
              Los gramos se calculan en <strong>metal fino con provisión PFC (+28 %)</strong>. El{" "}
              <strong>importe</strong> de cada celda = gramos × precio €/g del metal (chips de arriba).
              Oro, platino y plata: <strong>gold-api.com</strong>. Titanio: Rzzro (si disponible) o precio
              manual en <code>MetalNeedHorizonConfig.js</code>.
            </Typography>
          </Stack>
        </Popover>

        <TableContainer sx={{ boxShadow: "none" }}>
          <Table size="small" sx={horizonTableSx}>
            <TableHead>
              <TableRow>
                <TableCell rowSpan={2} className="horizon-col-metal" align="center" sx={{ verticalAlign: "middle" }}>
                  Metal
                </TableCell>
                {bucketDefs.map((bucket, bucketIdx) => (
                  <TableCell
                    key={bucket.key}
                    colSpan={HORIZON_SUB_COLS}
                    align="center"
                    className={periodDividerClass(bucketIdx)}
                  >
                    <Box component="span" sx={{ display: "block", fontWeight: 600, color: HORIZON_UI.headerText, fontSize: TB.font, lineHeight: 1.25 }}>
                      {bucket.label}
                    </Box>
                    <Box component="span" sx={{ display: "block", color: HORIZON_UI.headerTextMuted, fontWeight: 400, fontSize: TB.fontSm, lineHeight: 1.2 }}>
                      {bucket.dateRangeLabel}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                {bucketDefs.map((bucket, bucketIdx) => (
                  <React.Fragment key={`${bucket.key}-sub`}>
                    <TableCell className="horizon-data-col horizon-sub-nec" align="center">Nec.</TableCell>
                    <TableCell className="horizon-data-col horizon-sub-stock" align="center">Stock</TableCell>
                    <TableCell className={`horizon-data-col horizon-sub-pend ${periodDividerClass(bucketIdx)}`} align="center">Pend.</TableCell>
                  </React.Fragment>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {metals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={1 + bucketDefs.length * HORIZON_SUB_COLS}>
                    <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic", py: 1 }}>
                      No hay pedidos con fecha de entrega válida.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                metals.map((metalRow) => {
                  let cumulativeGrams = 0;
                  const eurPerGram = getMetalEurPerGram(metalRow.key);

                  const bucketMetrics = bucketDefs.map((bucket) => {
                    const cell = metalRow.buckets[bucket.key];
                    const cumulativeBefore = cumulativeGrams;
                    cumulativeGrams += cell?.grams || 0;
                    return {
                      key: bucket.key,
                      bucket,
                      m: getHorizonBucketMetrics(cell, {
                        eurPerGram,
                        metalKey: metalRow.key,
                        cumulativeGrams,
                        cumulativeGramsBefore: cumulativeBefore,
                      }),
                    };
                  });

                  const showStatus = (m) =>
                    !m.empty && (m.cumNeed > 0 || m.grams > 0 || m.falta > 0 || m.orders > 0);

                  return (
                    <React.Fragment key={metalRow.key}>
                      <TableRow>
                        <TableCell
                          rowSpan={2}
                          className="horizon-col-metal"
                          sx={{ verticalAlign: "middle" }}
                        >
                          <Tooltip title={metalRow.label} arrow>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75 }}>
                              {renderMetalSphere(metalRow.key)}
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: TB.font,
                                  lineHeight: 1.2,
                                  overflowWrap: "anywhere",
                                  color: HORIZON_UI.headerText,
                                }}
                              >
                                {METAL_LABEL[metalRow.key] || metalRow.label}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </TableCell>
                        {bucketMetrics.map(({ key, m }, bucketIdx) => {
                          if (m.empty) {
                            return (
                              <React.Fragment key={key}>
                                <TableCell className="horizon-data-col horizon-sub-nec">{horizonEmptyCell()}</TableCell>
                                <TableCell className="horizon-data-col horizon-sub-stock">{horizonEmptyCell()}</TableCell>
                                <TableCell className={`horizon-data-col horizon-sub-pend ${periodDividerClass(bucketIdx)}`}>{horizonEmptyCell()}</TableCell>
                              </React.Fragment>
                            );
                          }

                          return (
                            <React.Fragment key={key}>
                              <TableCell className="horizon-data-col horizon-sub-nec" align="center">
                                {horizonSubCellValues(`${fmtNum(m.grams)} g`, m.amountStr)}
                              </TableCell>
                              <TableCell className="horizon-data-col horizon-sub-stock" align="center">
                                {m.showStockCompare ? (
                                  horizonSubCellValues(`${fmtNum(m.stockRemaining)} g`, m.dispEur)
                                ) : (
                                  horizonEmptyCell()
                                )}
                              </TableCell>
                              <TableCell className={`horizon-data-col horizon-sub-pend ${periodDividerClass(bucketIdx)}`} align="center">
                                {m.showStockCompare && m.falta > 0 ? (
                                  horizonSubCellValues(`${fmtNum(m.falta)} g`, m.pendEur, { error: true })
                                ) : (
                                  horizonEmptyCell()
                                )}
                              </TableCell>
                            </React.Fragment>
                          );
                        })}
                      </TableRow>
                      <TableRow>
                        {bucketMetrics.map(({ key, bucket, m }, bucketIdx) => (
                          <TableCell
                            key={`${key}-status`}
                            colSpan={HORIZON_SUB_COLS}
                            className={`horizon-data-col ${periodDividerClass(bucketIdx)}`}
                            align="center"
                            sx={{ py: 0.35, pt: 0, borderTop: "none" }}
                          >
                            {showStatus(m) &&
                              horizonStatusLine(m.statusWord, m.statusColor, m.orders, {
                                onOrdersClick: () => openHorizonBucketOrdersModal(metalRow, bucket, m),
                              })}
                          </TableCell>
                        ))}
                      </TableRow>
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // Calendario de cobertura reutilizable (normal y PFC según provisionFactor)
  const renderCoverageCalendar = (provisionFactor, label, accentColor, description, iconName) => {
    if (excelDetailsData.length === 0) return null;
    return (
      <Box
        sx={{
          flex: "1 1 420px",
          minWidth: 320,
          borderRadius: 1.5,
          border: `1px solid ${ESTADO_ORO_COLORS.SUMMARY_BORDER}`,
          backgroundColor: "#ffffff",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        {/* Cabecera */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 1,
            borderLeft: `4px solid ${accentColor}`,
            backgroundColor: `${accentColor}14`,
          }}
        >
          <MaterialCommunityIcons name={iconName} size={20} color={accentColor} />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", fontSize: "0.8rem", color: ESTADO_ORO_COLORS.SUMMARY_TEXT, lineHeight: 1.2 }}>
              {label}
            </Typography>
            <Typography variant="caption" sx={{ display: "block", color: "text.secondary", fontSize: "0.66rem", lineHeight: 1.3 }}>
              {description}
            </Typography>
          </Box>
        </Box>

        {/* Calendario (mitad) + detalle del día (mitad) */}
        <Box sx={{ p: 1.5, pb: 0.5, flex: 1 }}>
          <ComponentMetalsCalendar
            title=""
            compact
            size="sm"
            showTitle={false}
            sideDetails
            onOrderClick={handleOrderClick}
            provisionFactor={provisionFactor}
            baseStock={{
              cordobaGold: parseFloat(cordobaData.stock?.find((i) => i.metal === "ORO")?.stock || 0),
              cordobaPlatinum: parseFloat(cordobaData.stock?.find((i) => i.metal === "PLATINO")?.stock || 0),
              euroshineGold: calculateTotals(indiaData.goldStock || [], "gold").pureWt,
              euroshinePlatinum: calculateTotals(indiaData.platinumStock || [], "platinum").pureWt,
            }}
            details={mapExcelToCalendarDetails(excelDetailsData)}
            excelData={excelDetailsData}
            StockTotalOro={stockTotalOro}
            StockTotalPlatino={stockTotalPlatino}
          />
        </Box>

        {/* Leyenda */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", px: 1.5, py: 1, borderTop: `1px solid ${ESTADO_ORO_COLORS.SUMMARY_BORDER}` }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: 0.5, backgroundColor: "#d4edda", border: "1px solid #b7dfc0" }} />
            <Typography variant="caption" sx={{ fontSize: "0.66rem", color: "text.secondary" }}>
              Stock cubre el día
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: 0.5, backgroundColor: "#f8d7da", border: "1px solid #f0b6bc" }} />
            <Typography variant="caption" sx={{ fontSize: "0.66rem", color: "text.secondary" }}>
              Déficit (falta metal)
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ fontSize: "0.64rem", color: "text.secondary", fontStyle: "italic", ml: "auto" }}>
            Clic en un día para ver el detalle
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Módulo "Cobertura del stock" oculto a petición
      {renderCoverageSection()} */}

      {/* Resumen de metales a ancho completo */}
      {renderSummaryTable()}

      {/* Previsión de necesidad por horizontes de 6 semanas */}
      {renderMetalNeedHorizons()}
      {renderHorizonOrdersModal()}

      {/* Calendario de cobertura con provisión PFC */}
      {excelDetailsData.length > 0 && (
        <Box sx={{ display: "flex", mb: 2 }}>
          {renderCoverageCalendar(
            1.28,
            "Calendario · con provisión PFC (+28%)",
            "#b26a00",
            "Cuándo el stock deja de cubrir los pedidos aplicando el 28% de provisión para fundición.",
            "calendar-alert"
          )}
        </Box>
      )}

      {renderHelpBanner()}

      {/* Tabla de filtros Excel */}
      {excelDetailsData.length > 0 && (
        <ComponentExcelFilters 
          data={excelDetailsData} 
          title="Pedidos (Detalles de Órdenes y Artículos)"
          subtitle="Origen: Excel que llega cada noche. Filtra por fecha, cliente, metal, etc. para recalcular el requerido del resumen superior."
          onRequiredMetalsChange={setDynamicRequiredMetals}
        />
      )}

      {/* Solo mostrar India - Córdoba está comentada */}
      <Box sx={{ flex: 1 }}>
        {renderLocationSection("Euroshine", indiaData, "secondary")}
      </Box>
    </Box>
  );
}
